import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import Navigation from "../features/landing/shared/Navigation";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { logoBinary } from "../data/logoBinary"; // Assuming logoBase64 is imported from data/logo.js
const logoBase64 = logoBinary.logoBase64;

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

// Generates a detailed investment plan template based on quiz answers
const generatePlanTemplate = (row) => {
  // Asset allocation logic (simple example, can be made more sophisticated)
  let allocation = [];
  let summary = `Profile Summary\n`;
  summary += `Name: ${row.name}\nAge Group: ${row.ageGroup}\nGoal: ${row.goal}\nMonthly Investment: ${row.monthlyInvestment}\nDuration: ${row.duration}\nRisk: ${row.risk}`;

  // Default allocation
  let sip = row.monthlyInvestment || "(amount not specified)";
  if (row.risk && row.risk.toLowerCase().includes("low")) {
    allocation = [
      { asset: "Large Cap Equity Funds", percent: 40 },
      { asset: "Hybrid/Balanced Funds", percent: 30 },
      { asset: "Debt Funds", percent: 20 },
      { asset: "Gold/Safe Assets", percent: 5 },
      { asset: "Liquid Funds", percent: 5 },
    ];
  } else if (row.risk && row.risk.toLowerCase().includes("high")) {
    allocation = [
      { asset: "Mid/Small Cap Equity Funds", percent: 40 },
      { asset: "Large Cap Equity Funds", percent: 30 },
      { asset: "International Funds", percent: 10 },
      { asset: "Hybrid/Balanced Funds", percent: 10 },
      { asset: "Gold/Other", percent: 10 },
    ];
  } else {
    allocation = [
      { asset: "Large Cap Equity Funds", percent: 30 },
      { asset: "Mid Cap Equity Funds", percent: 20 },
      { asset: "Hybrid/Balanced Funds", percent: 20 },
      { asset: "Debt Funds", percent: 20 },
      { asset: "Gold/Other", percent: 10 },
    ];
  }

  // Plan recommendation
  let plan = `\n\nInvestment Plan Recommendation\n`;
  if (row.goal && row.goal.toLowerCase().includes("tax")) {
    plan +=
      "- Consider ELSS mutual funds for tax saving and long-term growth.\n";
  }
  if (row.duration && row.duration.includes("1 year")) {
    plan += "- For short-term, prioritize liquid and debt funds.\n";
  } else if (row.duration && row.duration.includes("5+")) {
    plan += "- For long-term, focus on equity-oriented funds for growth.\n";
  }
  plan += "- SIP is recommended for disciplined investing.\n";

  // Allocation table
  let allocationText = "\nSuggested Investment Allocation:";
  allocation.forEach((a) => {
    allocationText += `\n- ${a.asset}: ${a.percent}%`;
  });
  allocationText += `\n\nSuggested Monthly SIP: ${sip}`;

  // Annual review
  let review =
    "\n\nAnnual Review: We recommend reviewing your portfolio every year to adjust for life changes, market conditions, and goals.";

  return `${summary}${plan}${allocationText}${review}\n\nLet me know if you have any questions!`;
};

// Helper to render HTML plan for modal
export const renderPlanHtml = (row) => {
  let allocation = [];
  let sip = row.monthlyInvestment || "(amount not specified)";
  if (row.risk && row.risk.toLowerCase().includes("low")) {
    allocation = [
      { asset: "Large Cap Equity Funds", percent: 40 },
      { asset: "Hybrid/Balanced Funds", percent: 30 },
      { asset: "Debt Funds", percent: 20 },
      { asset: "Gold/Safe Assets", percent: 5 },
      { asset: "Liquid Funds", percent: 5 },
    ];
  } else if (row.risk && row.risk.toLowerCase().includes("high")) {
    allocation = [
      { asset: "Mid/Small Cap Equity Funds", percent: 40 },
      { asset: "Large Cap Equity Funds", percent: 30 },
      { asset: "International Funds", percent: 10 },
      { asset: "Hybrid/Balanced Funds", percent: 10 },
      { asset: "Gold/Other", percent: 10 },
    ];
  } else {
    allocation = [
      { asset: "Large Cap Equity Funds", percent: 30 },
      { asset: "Mid Cap Equity Funds", percent: 20 },
      { asset: "Hybrid/Balanced Funds", percent: 20 },
      { asset: "Debt Funds", percent: 20 },
      { asset: "Gold/Other", percent: 10 },
    ];
  }
  // Pie chart data
  const pieData = {
    labels: allocation.map((a) => a.asset),
    datasets: [
      {
        data: allocation.map((a) => a.percent),
        backgroundColor: [
          "#60a5fa", // blue-400
          "#34d399", // green-400
          "#fbbf24", // yellow-400
          "#a78bfa", // purple-400
          "#f87171", // red-400
        ],
        borderWidth: 1,
      },
    ],
  };
  const pieOptions = {
    plugins: {
      legend: {
        display: false,
        position: "right",
        labels: { font: { size: 6 } },
      },
      datalabels: {
        color: "#222",
        font: { weight: "bold", size: 10 },
        formatter: (value, ctx) => value + "%",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.label || "";
            let value = context.parsed || 0;
            return `${label}: ${value}%`;
          },
        },
      },
    },
    maintainAspectRatio: false,
    responsive: true,
    cutout: 0,
  };
  // Potential growth
  const growthText = estimatePotentialGrowth(sip, row.duration, allocation);
  return (
    <div className="space-y-3 text-sm">
      {" "}
      {/* Reduced gap from space-y-4 to space-y-3 for tighter, more balanced look */}
      {/* Profile Summary as a table */}
      <div className="font-bold text-base bg-blue-50 rounded px-3 py-1 border border-blue-100">
        Based on your answers {row.name}, here's a smart way to start your
        investment journey!
      </div>
      <div className="mb-1 bg-blue-50 rounded px-3 border border-blue-100">
        <table className="w-full text-xs">
          <tbody>
            <tr>
              <td className="font-semibold w-1/3">Name</td>
              <td className="">{row.name}</td>
            </tr>
            <tr>
              <td className="font-semibold">Age Group</td>
              <td className="">{row.ageGroup}</td>
            </tr>
            <tr>
              <td className="font-semibold">Goal</td>
              <td className="">{row.goal}</td>
            </tr>
            <tr>
              <td className="font-semibold">Monthly Investment</td>
              <td className="">{row.monthlyInvestment}</td>
            </tr>
            <tr>
              <td className="font-semibold">Duration</td>
              <td className="">{row.duration}</td>
            </tr>
            <tr>
              <td className="font-semibold">Risk</td>
              <td className="">{row.risk}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="font-semibold text-base bg-green-50 rounded px-3 py-2 border border-green-100">
        Investment Plan Recommendation
      </div>{" "}
      {/* Removed mt-2 */}
      <ul className="mb-1 bg-green-50 rounded px-3 py-2 border border-green-100">
        {" "}
        {/* Reduced mb-2 to mb-1 */}
        {row.goal && row.goal.toLowerCase().includes("tax") && (
          <li>
            <span className="inline-block w-4 h-4 mr-2 border border-gray-400 rounded flex-shrink-0 relative">
              <span className="absolute inset-0 flex items-center justify-center">
                ✓
              </span>
            </span>
            Consider ELSS mutual funds for tax saving and long-term growth.
          </li>
        )}
        {row.duration && row.duration.includes("1 year") && (
          <li>
            <span className="inline-block w-4 h-4 mr-2 border border-gray-400 rounded flex-shrink-0 relative">
              <span className="absolute inset-0 flex items-center justify-center">
                ✓
              </span>
            </span>
            For short-term, prioritize liquid and debt funds.
          </li>
        )}
        {row.duration && row.duration.includes("5+") && (
          <li>
            <span className="inline-block w-4 h-4 mr-2 border border-gray-400 rounded flex-shrink-0 relative">
              <span className="absolute inset-0 flex items-center justify-center">
                ✓
              </span>
            </span>
            For long-term, focus on equity-oriented funds for growth.
          </li>
        )}
        <li>
          <span className="inline-block w-4 h-4 mr-2 border border-gray-400 rounded flex-shrink-0 relative">
            <span className="absolute inset-0 flex items-center justify-center">
              ✓
            </span>
          </span>
          SIP is recommended for disciplined investing.
        </li>
      </ul>
      <div className="font-semibold text-base bg-yellow-50 rounded px-3 py-2 border border-yellow-100">
        Monthly SIP: <b>{sip}</b>
      </div>
      {/* Removed mt-2 */}
      <div className="bg-yellow-50 rounded px-3 py-2 border border-yellow-100">
        <h2>
          <b>Suggested Investment Allocation</b>
        </h2>

        <div className="flex flex-col md:flex-row md:items-center md:gap-6">
          <div className="w-full md:w-1/2">
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[500px] text-xs border mb-2">
                <thead>
                  <tr>
                    <th className="text-left">Asset Class</th>
                    <th className="text-left">Allocation (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {allocation.map((a, i) => (
                    <tr key={i}>
                      <td className="text-left">{a.asset}</td>
                      <td className="text-left">{a.percent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="w-full md:w-1/2 flex flex-col items-center justify-center min-h-[140px]">
            {" "}
            {/* Reduced min-h from 160px to 140px for balance */}
            <div className="w-[120px] h-[120px]">
              {" "}
              {/* Reduced chart size for better spacing */}
              <Pie
                data={pieData}
                options={pieOptions}
                width={120}
                height={120}
              />
            </div>
            <div className="mt-1 text-xs text-gray-700 text-center">
              Asset Allocation Breakdown
            </div>{" "}
            {/* Reduced mt-2 to mt-1 */}
          </div>
        </div>
      </div>
      {/* Removed mt-2 */}
      <div className="bg-orange-50 rounded px-3 py-2 border border-orange-100">
        <h2 className="font-semibold text-base py-2">Potential Growth</h2>
      </div>
      <div className="bg-orange-50 rounded px-3 py-2 border border-orange-100">
        {(() => {
          const sipVals = parseRange(sip);
          let yearVals = [5];
          if (typeof row.duration === "string" && row.duration.includes("5+"))
            yearVals = [6, 7, 8, 9, 10, 11, 12, 13];
          else if (
            typeof row.duration === "string" &&
            row.duration.includes("1 year")
          )
            yearVals = [0.5, 1];
          else if (
            typeof row.duration === "string" &&
            row.duration.match(/(\d+)[–-](\d+)/)
          ) {
            const m = row.duration.match(/(\d+)[–-](\d+)/);
            yearVals = [parseInt(m[1], 10), parseInt(m[2], 10)];
          } else if (
            typeof row.duration === "string" &&
            row.duration.match(/\d+/)
          ) {
            yearVals = [parseInt(row.duration.match(/\d+/)[0])];
          } else if (typeof row.duration === "number") {
            yearVals = [row.duration];
          }
          return sipVals.map((sipNum, idx) => (
            <div key={idx} className="mb-4">
              <div className="font-semibold mb-1 text-orange-700">
                Monthly SIP: ₹{sipNum.toLocaleString()}
              </div>
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[500px] text-xs border mb-2">
                  <thead>
                    <tr className="bg-orange-100">
                      <th className="text-center">SIP Amount</th>
                      <th className="text-center">Duration (years)</th>
                      <th className="text-center">Potential Corpus</th>
                      <th className="text-center">Total Invested</th>
                      <th className="text-center">Estimated Returns</th>
                      <th className="text-center">Estimated Returns(%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearVals.map((n, i) => {
                      let avgReturn = 0.12;
                      if (allocation.some((a) => a.asset.includes("Debt")))
                        avgReturn = 0.08;
                      if (allocation.some((a) => a.asset.includes("Mid/Small")))
                        avgReturn = 0.14;
                      let fv =
                        sipNum *
                        12 *
                        ((Math.pow(1 + avgReturn, n) - 1) / avgReturn) *
                        (1 + avgReturn);
                      return (
                        <tr
                          key={i}
                          className={
                            i % 2 === 0 ? "bg-orange-50" : "bg-orange-100"
                          }
                        >
                          <td className="text-center">
                            ₹{Math.round(sipNum).toLocaleString()}
                          </td>
                          <td className="text-center">{n}</td>
                          <td className="font-bold text-green-700 text-center">
                            ₹{Math.round(fv).toLocaleString()}
                          </td>
                          <td className="text-center">
                            ₹{Math.round(n * 12 * sipNum).toLocaleString()}
                          </td>

                          <td className="text-center">
                            ₹{Math.round(fv - n * 12 * sipNum).toLocaleString()}
                          </td>
                          <td className="text-center">
                            {(
                              ((fv - n * 12 * sipNum) / (n * 12 * sipNum)) *
                              100
                            ).toFixed(2)}
                            %
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ));
        })()}
        <div className="text-xs text-gray-700">
          Assuming ~
          {allocation.some((a) => a.asset.includes("Debt"))
            ? 8
            : allocation.some((a) => a.asset.includes("Mid/Small"))
            ? 14
            : 12}
          % annualized returns. Actual results may vary.
        </div>
      </div>
      <div className="bg-purple-50 rounded px-3 py-2 border border-purple-100">
        <b>Annual Review:</b> We recommend reviewing your portfolio every year
        to adjust for life changes, market conditions, and goals.
      </div>{" "}
      {/* Removed mt-2 */}
    </div>
  );
};

// PDF generation
const generatePdf = (row) => {
  const doc = new jsPDF();
  let y = 14;

  // Add BuildMyWealth logo at the top (if available)
  try {
    if (logoBase64 && logoBase64.startsWith("data:image")) {
      doc.addImage(logoBase64, "PNG", 80, y, 50, 18, undefined, "FAST");
      y += 22;
    } else {
      y += 4;
    }
  } catch (e) {
    y += 4;
  }

  // Personalized heading
  doc.setFontSize(15);
  doc.setTextColor(41, 128, 185);
  doc.text(
    `Personalized Investment Plan Analysis for ${row.name || "Client"}`,
    14,
    y
  );
  y += 10;

  // Section: Summary (Blue)
  doc.setFillColor(219, 234, 254); // blue-50
  doc.roundedRect(10, y, 190, 10, 2, 2, "F");
  doc.setFontSize(12);
  doc.setTextColor(30, 64, 175); // blue-800
  doc.text("Summary", 14, y + 7);
  y += 14;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.text(`Name: ${row.name || ""}`, 14, y);
  doc.text(`Age Group: ${row.ageGroup || ""}`, 80, y);
  y += 6;
  doc.text(`Goal: ${row.goal || ""}`, 14, y);
  doc.text(`Monthly Investment: ${row.monthlyInvestment || ""}`, 80, y);
  y += 6;
  doc.text(`Duration: ${row.duration || ""}`, 14, y);
  doc.text(`Risk: ${row.risk || ""}`, 80, y);
  y += 8;

  // Section: Investment Plan Recommendation (Green)
  doc.setFillColor(220, 252, 231); // green-50
  doc.roundedRect(10, y, 190, 10, 2, 2, "F");
  doc.setFontSize(12);
  doc.setTextColor(21, 128, 61); // green-700
  doc.text("Investment Plan Recommendation", 14, y + 7);
  y += 14;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  let planLines = [];
  if (row.goal && row.goal.toLowerCase().includes("tax"))
    planLines.push(
      "- Consider ELSS mutual funds for tax saving and long-term growth."
    );
  if (row.duration && row.duration.includes("1 year"))
    planLines.push("- For short-term, prioritize liquid and debt funds.");
  else if (row.duration && row.duration.includes("5+"))
    planLines.push(
      "- For long-term, focus on equity-oriented funds for growth."
    );
  planLines.push("- SIP is recommended for disciplined investing.");
  planLines.forEach((line) => {
    doc.text(line, 14, y);
    y += 6;
  });
  y += 2;

  // Section: Suggested Investment Allocation (Yellow)
  doc.setFillColor(254, 243, 199); // yellow-50
  doc.roundedRect(10, y, 190, 10, 2, 2, "F");
  doc.setFontSize(12);
  doc.setTextColor(202, 138, 4); // yellow-700
  doc.text("Suggested Investment Allocation", 14, y + 7);
  y += 14;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  let allocation = [];
  if (row.risk && row.risk.toLowerCase().includes("low")) {
    allocation = [
      { asset: "Large Cap Equity Funds", percent: 40 },
      { asset: "Hybrid/Balanced Funds", percent: 30 },
      { asset: "Debt Funds", percent: 20 },
      { asset: "Gold/Safe Assets", percent: 5 },
      { asset: "Liquid Funds", percent: 5 },
    ];
  } else if (row.risk && row.risk.toLowerCase().includes("high")) {
    allocation = [
      { asset: "Mid/Small Cap Equity Funds", percent: 40 },
      { asset: "Large Cap Equity Funds", percent: 30 },
      { asset: "International Funds", percent: 10 },
      { asset: "Hybrid/Balanced Funds", percent: 10 },
      { asset: "Gold/Other", percent: 10 },
    ];
  } else {
    allocation = [
      { asset: "Large Cap Equity Funds", percent: 30 },
      { asset: "Mid Cap Equity Funds", percent: 20 },
      { asset: "Hybrid/Balanced Funds", percent: 20 },
      { asset: "Debt Funds", percent: 20 },
      { asset: "Gold/Other", percent: 10 },
    ];
  }
  autoTable(doc, {
    startY: y,
    head: [["Asset Class", "Allocation (%)"]],
    body: allocation.map((a) => [a.asset, a.percent + "%"]),
    theme: "grid",
    styles: { fontSize: 10 },
    headStyles: { fillColor: [202, 138, 4], textColor: 255 }, // yellow-700
    alternateRowStyles: { fillColor: [254, 243, 199] }, // yellow-50
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 4;
  doc.setFont("helvetica", "bold");
  doc.text(`Suggested Monthly SIP: ${row.monthlyInvestment || ""}`, 14, y);
  doc.setFont("helvetica", "normal");
  y += 8;

  // Section: Potential Growth (Orange)
  doc.setFillColor(255, 237, 213); // orange-50
  doc.roundedRect(10, y, 190, 10, 2, 2, "F");
  doc.setFontSize(12);
  doc.setTextColor(194, 65, 12); // orange-700
  doc.text("Potential Growth", 14, y + 7);
  y += 14;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  // Growth Table
  const sipVals = parseRange(row.monthlyInvestment || "0");
  let yearVals = [5];
  if (typeof row.duration === "string" && row.duration.includes("5+"))
    yearVals = [7];
  else if (typeof row.duration === "string" && row.duration.includes("1 year"))
    yearVals = [0.5, 1];
  else if (
    typeof row.duration === "string" &&
    row.duration.match(/(\d+)[–-](\d+)/)
  ) {
    const m = row.duration.match(/(\d+)[–-](\d+)/);
    yearVals = [parseInt(m[1], 10), parseInt(m[2], 10)];
  } else if (typeof row.duration === "string" && row.duration.match(/\d+/)) {
    yearVals = [parseInt(row.duration.match(/\d+/)[0])];
  } else if (typeof row.duration === "number") {
    yearVals = [row.duration];
  }
  let avgReturn = 0.12;
  if (allocation.some((a) => a.asset.includes("Debt"))) avgReturn = 0.08;
  if (allocation.some((a) => a.asset.includes("Mid/Small"))) avgReturn = 0.14;
  let growthRows = [];
  sipVals.forEach((sipNum) => {
    yearVals.forEach((n) => {
      let fv =
        sipNum *
        12 *
        ((Math.pow(1 + avgReturn, n) - 1) / avgReturn) *
        (1 + avgReturn);
      growthRows.push([
        `${sipNum.toLocaleString()}`,
        `${n}`,
        `${Math.round(sipNum * 12 * n).toLocaleString()}`,
        `${Math.round(fv).toFixed(2).toLocaleString()}`,
        `${Math.round(fv - sipNum * 12 * n)
          .toFixed(2)
          .toLocaleString()}`,
        `${(((fv - sipNum * 12 * n) / (sipNum * 12 * n)) * 100).toFixed(2)}%`,
      ]);
    });
  });
  autoTable(doc, {
    startY: y,
    head: [
      [
        "Monthly SIP(INR)",
        "Duration (years)",
        "Total Invested(INR)",
        "Potential Corpus(INR)",
        "Estimated Returns(INR)",
        "Estimated Returns (%)",
      ],
    ],
    body: growthRows,
    theme: "grid",
    styles: { fontSize: 10 },
    headStyles: { fillColor: [194, 65, 12], textColor: 255 }, // orange-700
    alternateRowStyles: { fillColor: [255, 237, 213] }, // orange-50
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 4;
  doc.setFontSize(9);
  doc.setTextColor(120, 53, 15); // orange-900
  doc.text(
    `Assuming ~${Math.round(avgReturn * 100).toFixed(
      2
    )}% annualized returns. Actual results may vary.`,
    14,
    y
  );
  y += 8;

  // Section: Annual Review (Purple)
  doc.setFillColor(237, 233, 254); // purple-50
  doc.roundedRect(10, y, 190, 10, 2, 2, "F");
  doc.setFontSize(12);
  doc.setTextColor(109, 40, 217); // purple-700
  doc.text("Annual Review", 14, y + 7);
  y += 14;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.text(
    "We recommend reviewing your portfolio every year to adjust for life changes, market conditions, and goals.",
    14,
    y,
    { maxWidth: 180 }
  );
  y += 10;

  // Reset font
  doc.setFont("helvetica", "normal");
  doc.save(`Investment_Plan_${row.name || "Client"}.pdf`);
};

// Helper to parse SIP and duration ranges
function parseRange(val) {
  if (typeof val !== "string") return [val];
  // Support for three-value ranges like "₹5,001–₹10,000–₹15,000"
  const allMatches = val.replace(/,/g, "").match(/\d+/g);
  if (allMatches && allMatches.length > 1) {
    // Always include the maximum value as the last value
    const nums = allMatches.map((n) => parseInt(n, 10));
    const max = Math.max(...nums);
    // Remove duplicates, keep order: lower, upper, max
    const unique = Array.from(new Set([...nums, max]));
    return unique;
  }
  if (allMatches && allMatches.length === 1) {
    return [parseInt(allMatches[0], 10)];
  }
  return [0];
}

// Returns array of {sipNum, years, corpusText, corpusValue}
function getGrowthProjections(sip, years, allocation) {
  let avgReturn = 0.12;
  if (allocation.some((a) => a.asset.includes("Debt"))) avgReturn = 0.08;
  if (allocation.some((a) => a.asset.includes("Mid/Small"))) avgReturn = 0.14;
  const sipVals = parseRange(sip);
  let yearVals = [5];
  if (typeof years === "string" && years.includes("5+")) yearVals = [7];
  else if (typeof years === "string" && years.includes("1 year"))
    yearVals = [1];
  else if (typeof years === "string" && years.match(/(\d+)[–-](\d+)/)) {
    const m = years.match(/(\d+)[–-](\d+)/);
    yearVals = [parseInt(m[1], 10), parseInt(m[2], 10)];
  } else if (typeof years === "string" && years.match(/\d+/)) {
    yearVals = [parseInt(years.match(/\d+/)[0])];
  } else if (typeof years === "number") {
    yearVals = [years];
  }
  const results = [];
  for (const s of sipVals) {
    for (const n of yearVals) {
      let fv =
        s *
        12 *
        ((Math.pow(1 + avgReturn, n) - 1) / avgReturn) *
        (1 + avgReturn);
      results.push({
        sipNum: s,
        years: n,
        corpusText: `₹${Math.round(fv).toLocaleString()}`,
        corpusValue: fv,
      });
    }
  }
  return results;
}

// Estimate potential growth for SIP
const estimatePotentialGrowth = (sip, years, allocation) => {
  // Returns an array of {sipNum, text}
  let avgReturn = 0.12; // 12% default
  if (allocation.some((a) => a.asset.includes("Debt"))) avgReturn = 0.08;
  if (allocation.some((a) => a.asset.includes("Mid/Small"))) avgReturn = 0.14;
  let n = parseInt(years) || 5;
  if (typeof years === "string" && years.includes("5+")) n = 7;
  if (typeof years === "string" && years.includes("1 year")) n = 1;
  if (typeof years === "string" && years.match(/\d+/))
    n = parseInt(years.match(/\d+/)[0]);

  let results = [];
  if (typeof sip === "string") {
    // Handle range like "₹5,001–₹10,000" or "₹5,001-₹10,000"
    const rangeMatch = sip.replace(/,/g, "").match(/(\d+)[–-](\d+)/);
    if (rangeMatch) {
      const low = parseInt(rangeMatch[1], 10);
      const high = parseInt(rangeMatch[2], 10);
      [low, high].forEach((sipNum) => {
        let fv =
          sipNum *
          12 *
          ((Math.pow(1 + avgReturn, n) - 1) / avgReturn) *
          (1 + avgReturn);
        results.push({
          sipNum,
          text: `If you invest ₹${sipNum.toLocaleString()} per month for ${n} years, your potential corpus could be ₹${Math.round(
            fv
          ).toLocaleString()} (assuming ~${Math.round(
            avgReturn * 100
          )}% annualized returns). Actual results may vary.`,
        });
      });
    } else {
      // Extract only the first group of digits (not all digits concatenated)
      const singleMatch = sip.replace(/,/g, "").match(/(\d+)/);
      const sipNum = singleMatch ? parseInt(singleMatch[1], 10) : 0;
      let fv =
        sipNum *
        12 *
        ((Math.pow(1 + avgReturn, n) - 1) / avgReturn) *
        (1 + avgReturn);
      results.push({
        sipNum,
        text: `If you invest ₹${sipNum.toLocaleString()} per month for ${n} years, your potential corpus could be ₹${Math.round(
          fv
        ).toLocaleString()} (assuming ~${Math.round(
          avgReturn * 100
        )}% annualized returns). Actual results may vary.`,
      });
    }
  } else {
    const sipNum = sip || 0;
    let fv =
      sipNum *
      12 *
      ((Math.pow(1 + avgReturn, n) - 1) / avgReturn) *
      (1 + avgReturn);
    results.push({
      sipNum,
      text: `If you invest ₹${sipNum.toLocaleString()} per month for ${n} years, your potential corpus could be ₹${Math.round(
        fv
      ).toLocaleString()} (assuming ~${Math.round(
        avgReturn * 100
      )}% annualized returns). Actual results may vary.`,
    });
  }
  return results;
};

const PAGE_SIZE = 10;

const InvestmentQuizResponses = () => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [suggestion, setSuggestion] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [page, setPage] = useState(1);
  const [lastVisible, setLastVisible] = useState(null);
  const [firstVisible, setFirstVisible] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [pageStack, setPageStack] = useState([]);

  const fetchPage = async (direction = "next") => {
    setLoading(true);
    let q;
    if (direction === "next" && lastVisible) {
      q = query(
        collection(db, "investmentQuizResponses"),
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        limit(PAGE_SIZE)
      );
    } else if (direction === "prev" && pageStack.length > 1) {
      // Go to previous page
      const prevLast = pageStack[pageStack.length - 2];
      q = query(
        collection(db, "investmentQuizResponses"),
        orderBy("createdAt", "desc"),
        startAfter(prevLast),
        limit(PAGE_SIZE)
      );
    } else {
      // First page
      q = query(
        collection(db, "investmentQuizResponses"),
        orderBy("createdAt", "desc"),
        limit(PAGE_SIZE)
      );
    }
    const snap = await getDocs(q);
    const docs = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setResponses(docs);
    setFirstVisible(snap.docs[0]);
    setLastVisible(snap.docs[snap.docs.length - 1]);
    setHasNext(snap.docs.length === PAGE_SIZE);
    setHasPrev(page > 1);
    if (direction === "next") {
      setPageStack((prev) => [...prev, snap.docs[0]]);
      setPage((p) => p + 1);
    } else if (direction === "prev") {
      setPageStack((prev) => prev.slice(0, -1));
      setPage((p) => Math.max(1, p - 1));
    } else {
      setPageStack([snap.docs[0]]);
      setPage(1);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPage("first");
    // eslint-disable-next-line
  }, []);

  const openModal = (row) => {
    setSelected(row);
    setSuggestion(generatePlanTemplate(row));
    setModalOpen(true);
  };

  const sendToWhatsApp = () => {
    if (!selected || !selected.whatsapp) return;
    const phone = selected.whatsapp.replace(/^0+/, "");
    const url = `https://wa.me/91${phone}?text=${encodeURIComponent(
      suggestion
    )}`;
    window.open(url, "_blank");
  };

  return (
    <>
      <Navigation />
      <div className="p-4 max-w-5xl mx-auto py-16 bg-gradient-to-br from-indigo-50 via-white to-purple-50 rounded-2xl shadow-2xl border border-indigo-100 min-h-[80vh]">
        <h1 className="text-3xl md:text-4xl font-extrabold mb-6 text-indigo-700 flex items-center gap-2">
          <svg
            className="w-8 h-8 text-indigo-500"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M13 16h-1v-4h-1m1-4h.01M12 20c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z" />
          </svg>
          Investment Quiz Responses
        </h1>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <svg
              className="animate-spin h-8 w-8 text-indigo-500 mr-2"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
            <span className="text-lg text-indigo-600 font-semibold">
              Loading...
            </span>
          </div>
        ) : responses.length === 0 ? (
          <div className="text-center text-gray-500 py-12 text-lg">
            No quiz responses found yet.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-md">
              <table className="min-w-full text-sm text-gray-800">
                <thead>
                  <tr className="bg-indigo-100 text-indigo-700">
                    <th className="border px-3 py-2 font-semibold">Name</th>
                    <th className="border px-3 py-2 font-semibold">WhatsApp</th>
                    <th className="border px-3 py-2 font-semibold">Age</th>
                    <th className="border px-3 py-2 font-semibold">Goal</th>
                    <th className="border px-3 py-2 font-semibold">Monthly</th>
                    <th className="border px-3 py-2 font-semibold">Duration</th>
                    <th className="border px-3 py-2 font-semibold">Risk</th>
                    <th className="border px-3 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {responses.map((row, idx) => (
                    <tr
                      key={row.id}
                      className={
                        idx % 2 === 0
                          ? "bg-white"
                          : "bg-indigo-50 hover:bg-indigo-100"
                      }
                    >
                      <td className="border px-3 py-2">{row.name}</td>
                      <td className="border px-3 py-2">{row.whatsapp}</td>
                      <td className="border px-3 py-2">{row.ageGroup}</td>
                      <td className="border px-3 py-2">{row.goal}</td>
                      <td className="border px-3 py-2">
                        {row.monthlyInvestment}
                      </td>
                      <td className="border px-3 py-2">{row.duration}</td>
                      <td className="border px-3 py-2">{row.risk}</td>
                      <td className="border px-3 py-2 text-center">
                        <button
                          onClick={() => openModal(row)}
                          className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-1.5 rounded-full shadow hover:from-indigo-600 hover:to-purple-600 transition font-semibold text-xs"
                        >
                          Analyse
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between items-center mt-4">
              <button
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                onClick={() => fetchPage("prev")}
                disabled={!hasPrev}
              >
                Previous
              </button>
              <span>Page {page}</span>
              <button
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                onClick={() => fetchPage("next")}
                disabled={!hasNext}
              >
                Next
              </button>
            </div>
          </>
        )}

        {/* Modal */}
        {modalOpen && selected && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl md:max-w-3xl relative mx-2 overflow-y-auto max-h-[90vh] border-2 border-indigo-200 animate-fadeIn">
              <button
                onClick={() => setModalOpen(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-indigo-600 text-2xl font-bold"
              >
                &times;
              </button>
              <h2 className="text-2xl font-extrabold mb-4 text-indigo-700 flex items-center gap-2">
                Personalized Suggestion
              </h2>
              {renderPlanHtml(selected)}

              <div className="flex gap-2 mt-4 justify-end">
                <button
                  onClick={sendToWhatsApp}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full font-semibold shadow"
                >
                  Send to WhatsApp
                </button>
                <button
                  onClick={() => generatePdf(selected)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full font-semibold shadow"
                >
                  Download PDF
                </button>
                <button
                  onClick={() => window.print()}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-full font-semibold shadow"
                >
                  Print
                </button>
                <button
                  onClick={() => setModalOpen(false)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-full font-semibold shadow"
                >
                  Close
                </button>
              </div>
              {/* SEBI Disclaimer */}
              <div className="mt-8 text-xs text-gray-500 border-t pt-4 text-center print:text-left">
                <span className="font-semibold text-red-600">
                  SEBI Disclaimer:
                </span>{" "}
                Mukesh Kumar Singh (BuildMyWealth) is a registered mutual fund
                distributor (ARN-175025) and not a SEBI-registered investment
                advisor. The information and suggestions provided are for
                informational purposes only and do not constitute investment
                advice. Please consult with a SEBI-registered advisor before
                making any investment decisions. Past performance is not
                indicative of future results. Mutual fund investments are
                subject to market risks. Read all scheme-related documents
                carefully.
                <div className="mt-4 text-indigo-700 font-semibold text-sm">
                  Powered by{" "}
                  <a
                    href="https://buildmywealth.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-indigo-900"
                  >
                    BuildMyWealth.net
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default InvestmentQuizResponses;
